/*
  SPEK, bouton de prévente sur produit épuisé.
  Voir snippets/spk-presale-button.liquid pour le contexte.
  Envoie deux requêtes à l'API client Klaviyo (aucune clé privée côté navigateur) :
    1. back-in-stock-subscriptions : abonne le courriel à la variante épuisée, ce qui déclenche
       le flow « back in stock » existant (métrique Subscribed to Back in Stock).
    2. subscriptions : ajoute le courriel à la liste principale avec la propriété « langue »,
       pour que les flows envoient le courriel dans la bonne langue.
*/
(function () {
  'use strict';

  if (customElements.get('spk-presale')) return;

  var KLAVIYO_REVISION = '2026-07-15';
  var KLAVIYO_BASE = 'https://a.klaviyo.com/client/';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function decodeEntities(text) {
    if (typeof text !== 'string' || text.indexOf('&') === -1) return text;
    var el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  }

  function klaviyoPost(path, companyId, payload) {
    return fetch(KLAVIYO_BASE + path + '?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'revision': KLAVIYO_REVISION
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (!res.ok) {
        return res.text().then(function (txt) {
          throw new Error('Klaviyo ' + path + ' ' + res.status + ' ' + txt);
        });
      }
      return res;
    });
  }

  class SpkPresale extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('[data-presale-form]');
      this.emailInput = this.querySelector('[data-presale-email]');
      this.errorEl = this.querySelector('[data-presale-error]');
      this.submitBtn = this.querySelector('[data-presale-submit]');
      this.stepForm = this.querySelector('[data-presale-step="form"]');
      this.stepSuccess = this.querySelector('[data-presale-step="success"]');
      this.strings = this.readStrings();
      this.unsubscribe = null;
    }

    connectedCallback() {
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this));
        this.emailInput.addEventListener('input', this.clearError.bind(this));
      }
      this.watchVariant();
    }

    disconnectedCallback() {
      if (typeof this.unsubscribe === 'function') this.unsubscribe();
    }

    /* Les chaînes passent par le filtre Liquid `t`, qui encode les apostrophes en entités HTML. */
    readStrings() {
      try {
        var node = this.querySelector('[data-presale-strings]');
        var raw = node ? JSON.parse(node.textContent) : {};
        var out = {};
        Object.keys(raw).forEach(function (key) { out[key] = decodeEntities(raw[key]); });
        return out;
      } catch (e) {
        return {};
      }
    }

    /* Suit le changement de variante du thème Broadcast (product-info publie variantChange). */
    watchVariant() {
      if (typeof window.subscribe !== 'function' || !window.theme || !window.theme.PUB_SUB_EVENTS) return;
      var self = this;
      this.unsubscribe = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, function (event) {
        var data = event && event.data ? event.data : {};
        if (data.sectionId && self.dataset.section && data.sectionId !== self.dataset.section) return;
        var variant = data.variant;
        if (!variant) {
          self.classList.remove('is-active');
          return;
        }
        self.dataset.variantId = variant.id;
        self.dataset.variantTitle = variant.title || '';
        self.classList.toggle('is-active', variant.available === false);
        self.resetToForm();
      });
    }

    resetToForm() {
      if (this.stepSuccess && !this.stepSuccess.hidden) {
        this.stepSuccess.hidden = true;
        this.stepForm.hidden = false;
      }
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
      this.emailInput.setAttribute('aria-invalid', 'true');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
      this.emailInput.removeAttribute('aria-invalid');
    }

    setBusy(busy) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = busy;
      this.submitBtn.setAttribute('aria-busy', busy ? 'true' : 'false');
    }

    onSubmit(event) {
      event.preventDefault();
      this.clearError();

      var email = (this.emailInput.value || '').trim();
      if (!email) return this.showError(this.strings.errorRequired || 'This field is required');
      if (!EMAIL_RE.test(email)) return this.showError(this.strings.errorInvalid || 'This email is not valid');

      var companyId = this.dataset.companyId;
      var listId = this.dataset.listId;
      var variantId = this.dataset.variantId;
      if (!companyId || !variantId) {
        return this.showError(this.strings.errorGeneric || 'Something went wrong');
      }

      var lang = this.dataset.lang || 'en';
      var self = this;
      this.setBusy(true);

      var backInStock = klaviyoPost('back-in-stock-subscriptions/', companyId, {
        data: {
          type: 'back-in-stock-subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
            channels: ['EMAIL']
          },
          relationships: {
            variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } }
          }
        }
      });

      var listSubscription = listId
        ? klaviyoPost('subscriptions/', companyId, {
            data: {
              type: 'subscription',
              attributes: {
                custom_source: this.dataset.source || 'Prévente bouton site',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } },
                      properties: {
                        langue: lang,
                        presale_product: this.dataset.productTitle || '',
                        presale_variant: this.dataset.variantTitle || '',
                        presale_variant_id: variantId
                      }
                    }
                  }
                }
              },
              relationships: { list: { data: { type: 'list', id: listId } } }
            }
          })
        : Promise.resolve();

      Promise.all([backInStock, listSubscription])
        .then(function () {
          self.stepForm.hidden = true;
          self.stepSuccess.hidden = false;
          self.form.reset();
          if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
            window.klaviyo.identify({ email: email, langue: lang });
          }
        })
        .catch(function (err) {
          console.warn('[spk-presale]', err);
          self.showError(self.strings.errorGeneric || 'Something went wrong');
        })
        .finally(function () {
          self.setBusy(false);
        });
    }
  }

  customElements.define('spk-presale', SpkPresale);

  /* Boutons secondaires (barre panier collante) : ouvrent la fenêtre du bouton principal. */
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-spk-presale-open]');
    if (!trigger) return;
    event.preventDefault();
    var section = trigger.dataset.section;
    var host = (section && document.querySelector('spk-presale[data-section="' + section + '"]')) || document.querySelector('spk-presale');
    var opener = host ? host.querySelector('[data-popup-open]') : null;
    if (opener) opener.click();
  });
})();
